/**
 * Cataloging Agent Service
 * Communicates with mod-cataloging-agent via Okapi.
 * Uses the ky instance from useOkapiKy() — handles auth headers automatically.
 */

const BASE = 'cataloging-agent';

// ── Assets ─────────────────────────────────────────────────────────────────
export const uploadAsset = async (ky, file, jobId = null) => {
  const formData = new FormData();
  formData.append('file', file);

  const url = jobId ? `${BASE}/assets?jobId=${jobId}` : `${BASE}/assets`;

  return ky.post(url, { body: formData }).json();
};

// ── Jobs ───────────────────────────────────────────────────────────────────
export const createJob = async (ky, { templateId, assetIds, instanceId }) => {
  return ky
    .post(`${BASE}/jobs`, {
      json: { templateId, assetIds, instanceId },
    })
    .json();
};

export const getJob = async (ky, jobId) => {
  return ky.get(`${BASE}/jobs/${jobId}`).json();
};

export const getJobEvents = async (ky, jobId, afterSequence = 0) => {
  return ky.get(`${BASE}/jobs/${jobId}/events?after=${afterSequence}`).json();
};

export const submitReview = async (ky, jobId, { decisions, revision }) => {
  return ky
    .post(`${BASE}/jobs/${jobId}/decisions`, {
      json: { decisions, revision },
    })
    .json();
};

export const getChangeSet = async (ky, jobId) => {
  return ky.get(`${BASE}/jobs/${jobId}/changeset`).json();
};

export const applyToForm = async (ky, jobId, fingerprint) => {
  return ky.post(`${BASE}/jobs/${jobId}/apply-to-form`, {
    json: { fingerprint },
  });
};

export const cancelJob = async (ky, jobId) => {
  return ky.post(`${BASE}/jobs/${jobId}/cancel`).json();
};

// ── Proposals ─────────────────────────────────────────────────────────────
export const getProposals = async (ky, jobId) => {
  return ky.get(`${BASE}/jobs/${jobId}/proposals`).json();
};

// ── LLM Capabilities ──────────────────────────────────────────────────────
export const getCapabilities = async (ky) => {
  return ky.get(`${BASE}/llm/capabilities`).json();
};

// ── SSE Stream ────────────────────────────────────────────────────────────
/**
 * Open an SSE stream for real-time job progress.
 * Returns an EventSource-like object with onmessage and close().
 * Falls back to polling if SSE is not supported.
 */
export const openJobStream = (
  okapiUrl,
  tenant,
  token,
  jobId,
  onEvent,
  onError,
) => {
  const url = `${okapiUrl}/${BASE}/jobs/${jobId}/stream`;

  try {
    // SSE requires EventSource — not available in all environments
    const es = new EventSource(url);

    es.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data !== '[DONE]') onEvent(data);
      } catch {
        /* ignore parse errors */
      }
    };

    es.onerror = (err) => {
      es.close();
      if (onError) onError(err);
    };

    return { close: () => es.close() };
  } catch {
    // Fallback: return null — caller should use polling via getJobEvents
    return null;
  }
};
